import { ReactNode, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import DataAvailabilityModal from '../components/DataAvailabilityModal';

interface DataAvailabilityEntry {
    isOpen: boolean;
    /** Opens the modal; focus returns to `trigger` on close, or to `fallbackTriggerRef` if it has left the DOM. */
    open: (trigger: RefObject<HTMLElement>) => void;
    modal: ReactNode;
}

/**
 * Owns the Data Availability modal's open state at the host, so the modal stays open when the tab bar re-fits and
 * unmounts the button that opened it (moving it into ⋯); on close, focus returns to that control, or to the ⋯ trigger
 * if the control has left the DOM.
 */
export function useDataAvailabilityEntry(orgId: string, fallbackTriggerRef?: RefObject<HTMLElement>): DataAvailabilityEntry {
    const [isOpen, setIsOpen] = useState(false);
    const openerRef = useRef<RefObject<HTMLElement> | null>(null);

    const open = useCallback((trigger: RefObject<HTMLElement>) => {
        openerRef.current = trigger;
        setIsOpen(true);
    }, []);
    const close = useCallback(() => setIsOpen(false), []);

    const triggerRef = useMemo<RefObject<HTMLElement>>(
        () => ({
            get current() {
                const opener = openerRef.current?.current;
                return opener?.isConnected ? opener : fallbackTriggerRef?.current ?? null;
            },
        }),
        [fallbackTriggerRef],
    );

    return {
        isOpen,
        open,
        modal: isOpen ? <DataAvailabilityModal orgId={orgId} triggerRef={triggerRef} onClose={close} /> : null,
    };
}
